import React from 'react';
import { motion } from 'framer-motion';
import '../styles/Legal.css';

// --- ANIMATION VARIANTS ---
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.07 } }
};
const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } }
};
const titleVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } }
};
const wordVariants = {
  hidden: { y: "100%" },
  visible: { y: 0, transition: { duration: 0.8, ease: 'easeOut' } }
};

const Privacy = () => {
  const pageTitle = "Privacy Policy";

  return (
    <div className="legal-pageWrapper">
      <motion.div
        className="legal-container"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.header className="legal-header" variants={itemVariants}>
          <motion.h1 className="legal-mainTitle" variants={titleVariants}>
            {pageTitle.split(' ').map((word, index) => (
              <span key={index} className="legal-titleWord">
                <motion.span variants={wordVariants}>{word}</motion.span>
              </span>
            ))}
          </motion.h1>
          <p className="legal-lastUpdated"><em>Last updated: August 04, 2024</em></p>
        </motion.header>

        <motion.div className="legal-content">
          <motion.p variants={itemVariants}>This Privacy Policy describes Our policies and procedures on the collection, use, and disclosure of Your information when You use the Service and tells You about Your privacy rights and how the law protects You.</motion.p>
          <motion.p variants={itemVariants}>We use Your Personal data to provide and improve the Service. By using the Service, You agree to the collection and use of information in accordance with this Privacy Policy.</motion.p>

          <motion.h2 variants={itemVariants}>Interpretation and Definitions</motion.h2>
          <motion.h3 variants={itemVariants}>Interpretation</motion.h3>
          <motion.p variants={itemVariants}>The words of which the initial letter is capitalized have meanings defined under the following conditions. The following definitions shall have the same meaning regardless of whether they appear in singular or in plural.</motion.p>
          <motion.h3 variants={itemVariants}>Definitions</motion.h3>
          <motion.p variants={itemVariants}>For the purposes of this Privacy Policy:</motion.p>
          <motion.ul variants={itemVariants}>
            <li><strong>Account</strong> means a unique account created for You to access our Service or parts of our Service.</li>
            <li><strong>Affiliate</strong> means an entity that controls, is controlled by, or is under common control with a party, where "control" means ownership of 50% or more of the shares, equity interest, or other securities entitled to vote.</li>
            <li><strong>Company</strong> (referred to as either "the Company", "We", "Us" or "Our") refers to Robert Falkenberg, Author, 19179 Blanco Rd, Suite 105, San Antonio, Texas 78258.</li>
            <li><strong>Cookies</strong> are small files placed on Your device that contain details of Your browsing history on a website.</li>
            <li><strong>Country</strong> refers to: Texas, United States.</li>
            <li><strong>Device</strong> means any device that can access the Service such as a computer, a cellphone or a digital tablet.</li>
            <li><strong>Personal Data</strong> is any information that relates to an identified or identifiable individual.</li>
            <li><strong>Service</strong> refers to the Website.</li>
            <li><strong>Service Provider</strong> means any natural or legal person who processes data on behalf of the Company.</li>
            <li><strong>Usage Data</strong> refers to data collected automatically, either generated by the use of the Service or from the Service infrastructure itself.</li>
            <li><strong>Website</strong> refers to Robert Falkenberg, accessible from <a href="http://www.robertfalkenberg.com" target="_blank" rel="noopener noreferrer">http://www.robertfalkenberg.com</a>.</li>
            <li><strong>You</strong> means the individual accessing or using the Service, or the company or other legal entity on behalf of which such individual is accessing or using the Service.</li>
          </motion.ul>

          <motion.h2 variants={itemVariants}>Collecting and Using Your Personal Data</motion.h2>
          <motion.h3 variants={itemVariants}>Types of Data Collected</motion.h3>
          <motion.h4 variants={itemVariants}>Personal Data</motion.h4>
          <motion.p variants={itemVariants}>While using Our Service, We may ask You to provide personally identifiable information that can be used to contact or identify You.</motion.p>
          <motion.ul variants={itemVariants}>
            <li>Email address</li>
            <li>Usage Data</li>
          </motion.ul>
          <motion.h4 variants={itemVariants}>Usage Data</motion.h4>
          <motion.p variants={itemVariants}>Usage Data is collected automatically when using the Service. It may include your device’s IP address, browser type, pages visited, and other diagnostic data.</motion.p>

          <motion.h2 variants={itemVariants}>Tracking Technologies and Cookies</motion.h2>
          <motion.p variants={itemVariants}>We use Cookies and similar tracking technologies to track the activity on Our Service and store certain information.</motion.p>
          <motion.ul variants={itemVariants}>
            <li><strong>Cookies or Browser Cookies:</strong> A cookie is a small file placed on Your Device. You can instruct your browser to refuse all Cookies.</li>
            <li><strong>Web Beacons:</strong> Certain sections of our Service and emails may contain small electronic files that permit the Company to count users.</li>
            <li><strong>Persistent vs. Session Cookies:</strong> Persistent Cookies remain on your device when you go offline, while Session Cookies are deleted after your browser is closed.</li>
          </motion.ul>

          <motion.h2 variants={itemVariants}>Use of Your Personal Data</motion.h2>
          <motion.p variants={itemVariants}>The Company may use Personal Data for various purposes, including to provide and maintain our Service, manage Your Account, contact You, offer special promotions, and for business transfers.</motion.p>
          
          <motion.h2 variants={itemVariants}>Sharing Your Personal Data</motion.h2>
          <motion.ul variants={itemVariants}>
            <li>With Service Providers to monitor and analyze the use of our Service.</li>
            <li>For business transfers, such as mergers or acquisitions.</li>
            <li>With Affiliates, requiring them to honor this Privacy Policy.</li>
            <li>With business partners to offer certain products, services, or promotions.</li>
            <li>With other users when personal information is shared publicly.</li>
            <li>With Your consent for any other purposes.</li>
          </motion.ul>

          <motion.h2 variants={itemVariants}>Retention of Your Personal Data</motion.h2>
          <motion.p variants={itemVariants}>The Company will retain Your Personal Data only for as long as necessary for the purposes set out in this Privacy Policy and to comply with legal obligations.</motion.p>
          
          <motion.h2 variants={itemVariants}>Transfer of Your Personal Data</motion.h2>
          <motion.p variants={itemVariants}>Your information may be transferred and maintained on computers located outside of your state, province, country or other governmental jurisdiction.</motion.p>

          <motion.h2 variants={itemVariants}>Deleting Your Personal Data</motion.h2>
          <motion.p variants={itemVariants}>You have the right to delete or request deletion of the Personal Data we have collected about You.</motion.p>

          <motion.h2 variants={itemVariants}>Disclosure of Your Personal Data</motion.h2>
          <motion.p variants={itemVariants}>The Company may disclose Your Personal Data if required by law or in response to valid requests by public authorities.</motion.p>

          <motion.h2 variants={itemVariants}>Security of Your Personal Data</motion.h2>
          <motion.p variants={itemVariants}>While we strive to protect Your Personal Data, no method of transmission over the Internet is 100% secure.</motion.p>

          <motion.h2 variants={itemVariants}>Children’s Privacy</motion.h2>
          <motion.p variants={itemVariants}>Our Service does not address anyone under the age of 13. We do not knowingly collect personal information from anyone under 13.</motion.p>

          <motion.h2 variants={itemVariants}>Links to Other Websites</motion.h2>
          <motion.p variants={itemVariants}>Our Service may contain links to third-party websites. We strongly advise You to review the Privacy Policy of every site You visit.</motion.p>

          <motion.h2 variants={itemVariants}>Changes to this Privacy Policy</motion.h2>
          <motion.p variants={itemVariants}>We may update this Privacy Policy from time to time and will notify You of any changes by posting the new Privacy Policy on this page.</motion.p>
          
          <motion.h2 variants={itemVariants}>Contact Us</motion.h2>
          <motion.p variants={itemVariants}>If you have any questions about this Privacy Policy, You can contact us:</motion.p>
          <motion.p variants={itemVariants}>By email: <a href="mailto:robertfalkenbergauthor@gmail.com">robertfalkenbergauthor@gmail.com</a></motion.p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Privacy;